
import {
  CSMLSortBy,
  CSMLImportSource,
  CSMLAssetSource,
} from '@bingads-webui-campaign-react/asset-library-model';
import { stringifySync } from '@bingads-webui/safe-json';

import { formatCivilDate, removeNullUndefined } from './util';

import { transformImageResponse } from './asset-response-transformers/image-response-transformer';

export const getRepository = ({
  imageBulkEditODataService,
  imageODataService,
  i18n,
  ianaTimeZone,
  timezoneUTCOffset,
}) => {
  const getImages = ({
    searchTerm,
    minWidth,
    minHeight,
    dateAddedStart,
    dateAddedEnd,
    importSource,
    sortBy,
    skip,
    count,
  }) => {
    //
    const args = {
      searchTerm: encodeURIComponent(searchTerm),
      minWidth,
      minHeight,
      dateAddedStart: dateAddedStart && formatCivilDate(i18n, dateAddedStart, ianaTimeZone, timezoneUTCOffset, '00:00:00'),
      dateAddedEnd: dateAddedEnd && formatCivilDate(i18n, dateAddedEnd, ianaTimeZone, timezoneUTCOffset, '23:59:59'),
      skip,
      count,
    };

    if (importSource === CSMLImportSource.Google) {
      args.sourceType = 'GoogleImported';
    } else if (importSource === CSMLImportSource.Facebook) {
      args.sourceType = 'FacebookImported';
    }

    if (sortBy === CSMLSortBy.ImageName) {
      args.sortBy = 'Text';
      args.sortDirection = 'asc';
    } else if (sortBy === CSMLSortBy.MostRecent) {
      args.sortBy = 'ModifiedTime';
      args.sortDirection = 'desc';
    }
    return imageODataService.getImages(args).then((res) => {
      const totalCount = res['@odata.count'] || 0;
      const images = (res.value || []).map(img => transformImageResponse(img, i18n));

      return { images, totalCount };
    });
  };

  const getImage = imageId =>
    imageODataService.getImageByIdAsync(imageId).then(res => transformImageResponse(res, i18n));

  const getImageCroppingTaskStatus = (imageId) => {
    const promise = imageBulkEditODataService.getImageCroppingTaskStatus(imageId).then((res) => {
      const totalCount = res['@odata.count'] || 0;
      const tasks = (res.value || []).map(tsk => ({
        id: `${tsk.AssetId}`,
        name: tsk.ImageText,
        url: tsk.ImageUrl,
        createdTime: tsk.CreatedTime,
        modifiedTime: tsk.ModifiedTime,
        stage: tsk.Stage,
        hasError: tsk.HasError,
        failureMessage: tsk.Checkpoint && tsk.Checkpoint.FailureMessage,
        percentComplete: tsk.Checkpoint && tsk.Checkpoint.PercentComplete,
        imageCroppingTaskStatusId: tsk.ImageCroppingTaskStatusId,
        isFinished: tsk.Stage === 'Completed' || tsk.Stage === 'CompletedWithErrors' || tsk.Stage === 'Failed',
      }));
      return { tasks, totalCount };
    });
    return promise;
  };

  const renameImage = ({ imageId, name }) => {
    const promise = imageODataService.updateImageAsync({ Id: imageId, Text: name });
    return promise;
  };

  const deleteImage = (imageId) => {
    const promise = imageODataService.deleteImageAsync({ IdString: `${imageId}` });
    return promise;
  };

  const cropImage = ({ imageId, cropSettings, placementSettings }) => {
    const promise = imageBulkEditODataService.cropImage({ imageId, cropSettings, placementSettings })
      .then(res => res.value);
    return promise;
  };

  const uploadImagesByUrl = ({ imageUrls }) => {
    const promise = imageODataService.bulkSaveImagesByUrl(imageUrls);
    return promise;
  };

  const uploadImage = ({
    imageData,
    activityLogger = null,
    updateCurrentProgress = null,
    imageOption = 'Image',
    name = '',
  }) => {
    const isLogo = imageOption === 'Logo';

    const promise = imageODataService.uploadImageAsync(imageData, activityLogger, updateCurrentProgress, isLogo, name);
    return promise;
  };

  const addCreative = ({ creativeData, baseId, sourceType }) => {
    const serialized = stringifySync(removeNullUndefined(creativeData));
    // todo: save original image and save original image url inside metadata
    const args = [{
      url: creativeData.url,
      metadata: serialized.replace(/,/g, encodeURIComponent(',')),
      baseId,
      sourceType: sourceType || CSMLAssetSource.AIGenerated,
    }];
    const promise = imageODataService.bulkSaveImagesByUrl(args);
    return promise;
  };

  return {
    getImages,
    getImage,
    getImageCroppingTaskStatus,
    renameImage,
    deleteImage,
    cropImage,
    uploadImagesByUrl,
    uploadImage,
    addCreative,
  };
};
